import type { DateTime } from 'luxon';

import type {
  GetYouthAdventuresResponse,
  GetYouthAwardsResponse,
  GetYouthMeritBadgesResponse,
  YouthRankAdvancement,
} from '@appTypes/esb';
import type { AdvancementAuditInfo } from '@shared/components/AdvancementAudit/AdvancementAuditTooltip';

/**
 * The raw per-youth advancement records, one per type. Only the rank type has a
 * named export; the rest are the element type of their list response
 */
export type YouthMeritBadge = GetYouthMeritBadgesResponse[number];
export type YouthAward = GetYouthAwardsResponse[number];
export type YouthAdventure = GetYouthAdventuresResponse[number];

/** The advancement types this card supports (no sea-scout electives) */
export type SupportedAdvancementType =
  | 'ranks'
  | 'meritBadges'
  | 'awards'
  | 'adventures';

/** antd `Form.Item` names for the summary form; keep in sync with `AdvSummaryFormData` */
export enum FormField {
  CompletionDate = 'completionDate',
  Approved = 'approved',
  CounselorApproved = 'counselorApproved',
  Awarded = 'awarded',
}

/** The controls the action bar can show, one per checkbox plus the date field */
export enum AdvancementAction {
  Approve = 'approve',
  Award = 'award',
  CounselorApprove = 'counselorApprove',
  CompletionDate = 'completionDate',
}

/** What the allowed-actions hooks return: the controls the viewer may not act on */
export interface AllowedActionsResult {
  /** The card shows all controls and greys these */
  disabledActions: AdvancementAction[];
}

/**
 * What the youth hook returns. A youth never edits an advancement at header level
 * then their per-rank tier reaches only the requirements inside it
 */
export interface YouthAllowedActionsResult extends AllowedActionsResult {
  /** The controls the viewer may not act on for an individual requirement */
  requirementDisabledActions: AdvancementAction[];
}

/**
 * The raw advancement, discriminated on `type` so a consumer that already has the
 * record can hand it in fully typed (`type="ranks" rank={…}`) and the save handler
 * can read type-specific metadata back off it
 */
export type AdvancementRawByType =
  | { type: 'ranks'; rank?: YouthRankAdvancement }
  | { type: 'meritBadges'; meritBadge?: YouthMeritBadge }
  | { type: 'awards'; award?: YouthAward }
  | { type: 'adventures'; adventure?: YouthAdventure };

/**
 * The normalized subset the card renders. `AdvancementAuditInfo` is folded in so
 * the same object drives the status tag's audit tooltip
 */
export interface AdvancementDetailsData extends AdvancementAuditInfo {
  id: number | string;
  name: string;
  imageUrl100?: string | null;
  status?: string | null;
  /** ESB sends a 0–1 fraction; always render it through `toPercent` */
  percentCompleted?: number | null;
}

/** One rank in the ladder, for the jump-to dots */
export interface AdvancementRankStep {
  id: number;
  name: string;
  /** The rank currently open */
  active: boolean;
}

/** Prev/next between ranks; omitted for merit badges, awards and adventures */
export interface AdvancementDetailsNavigation {
  prevLabel?: string;
  nextLabel?: string;
  onPrev?: () => void;
  onNext?: () => void;
  hasPrev: boolean;
  hasNext: boolean;
  /** The whole ladder, rendered as clickable dots */
  steps?: AdvancementRankStep[];
  onSelectStep?: (id: number) => void;
}

/**
 * The antd `Form` values. The container seeds these as `initialValues`; the
 * discriminated raw record rides along so the submit handler has full metadata
 */
export interface AdvSummaryFormData {
  completionDate: DateTime | null;
  approved: boolean;
  counselorApproved: boolean;
  awarded: boolean;
  rank?: YouthRankAdvancement;
  meritBadge?: YouthMeritBadge;
  award?: YouthAward;
  adventure?: YouthAdventure;
}

interface AdvancementDetailsCommon {
  advancement: AdvancementDetailsData;
  /** First load only; collapses the card into its skeleton */
  loading?: boolean;
  saving?: boolean;
  /**
   * Only to dim the card but not showing the skeleton
   */
  busy?: boolean;
  /**
   * Which controls render. Defaults to every control relevant to the type
   * (counselor-approve only for merit badges); pass a subset only to hide some
   */
  visibleActions?: AdvancementAction[];
  /** Visible controls to render disabled (the viewer can't act on them) */
  disabledActions?: AdvancementAction[];
  navigation?: AdvancementDetailsNavigation;
  /** Rendered below Save — e.g. the merit-badge counselor block or an alert */
  extraContent?: React.ReactNode;
}

/** Presentation props; assumes an ancestor antd `<Form>` */
export interface AdvancementDetailsProps extends AdvancementDetailsCommon {
  type: SupportedAdvancementType;
}

/** Container props; the raw record is optional — the container fetches it if absent */
export type AdvancementDetailsContainerProps = AdvancementRawByType & {
  advancementId: number | string;
  /** Scoutbook user id (`sb_user_id`), the id every youth ESB query is keyed on */
  userId: string;
  /** Restrict which controls render (defaults to all type-relevant) */
  visibleActions?: AdvancementAction[];
  /** Visible controls to disable; merged with the data-driven lock */
  disabledActions?: AdvancementAction[];
  extraContent?: React.ReactNode;
  /** Custom submit; when omitted the container saves through RTK */
  onSave?: (values: AdvSummaryFormData) => void;
};
